import React, { useCallback, useState } from 'react';
import {
    managementCreateAccount,
    managementUpdateAccountByUniqueIdentifier,
} from '../../core/_requests';
import { APIError, useAxios, setCurrentAccountUniqueIdentifierInLocalStorage } from '../../core/_axios';
import { Account } from '../../core/_models';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import FuneralHomeLogoUploader from '../../components/FuneralHomeLogoUploader';
import { updateCurrentAccount } from '../../core/_requests_v2';
import useLocalizedCopy from '../../hooks/useLocalizedCopy';

export default function ManageAccountForm({
    account = undefined,
    setAccount = undefined,
    isForCurrentAccount = false,
}: {
    account?: Account,
    setAccount?: (account: Account) => void,
    isForCurrentAccount?: boolean,
}) {
    const axios = useAxios();
    const navigate = useNavigate();

    const {
        currentUser
    } = useAuth();

    const { copy } = useLocalizedCopy();

    const [saving, setSaving] = useState(false);
    const [errorSaving, setErrorSaving] = useState<string | null>(null);
    const [saveSuccess, setSaveSuccess] = useState<string | null>(null);

    // Advanced mode state
    const [viewAdvancedOptions, setViewAdvancedOptions] = useState(false);
    const [showLogoUploader, setShowLogoUploader] = useState(false);
    
    // Check if user can edit
    // Allow editing for: superusers, admins, or marketing leads viewing their own account
    const canEdit = currentUser.is_superuser || 
                   currentUser.is_super_super_user || 
                   currentUser.is_agency_admin ||
                   (isForCurrentAccount && currentUser.public_data?.is_marketing_lead);

    const onManageAccountFormSubmitted = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (saving) {
            return;
        }
        setSaveSuccess(null);
        setErrorSaving(null);
        setSaving(true);
        const form = event.currentTarget;
        const formData = new FormData(form);
        const data = Object.fromEntries(formData.entries());
        const finalData = {
            ...data,
            // disable free trial is a boolean but comes in as "on" or "off" from the checkbox
            manually_disabled_free_trial: formData.get('manually_disabled_free_trial') === 'on',
            enable_free_access: formData.get('enable_free_access') === 'on',
        }
        if (!account) {
            try {
                const createdAccount = await managementCreateAccount(axios, finalData);
                // Work in the new account from here on. Every page scopes to the account kept in
                // local storage, so without this the Services, Signatures, iPads and Account pages
                // kept showing the account selected before, and an edit made there landed on it
                // (reported 2026-10-01: a new account's details written over an existing one).
                setCurrentAccountUniqueIdentifierInLocalStorage(createdAccount.unique_identifier);
                // A full load, so the header and every page pick up the new account.
                window.location.href = `/management/accounts/${createdAccount.unique_identifier}/details?created=1`;
            } catch (exception) {
                console.error(exception);
                const errorMessage = exception instanceof APIError ? exception.message : 'An unknown error has occurred. Please try again.';
                setErrorSaving(errorMessage);
                setSaving(false);
            }
        } else {
            try {
                if (isForCurrentAccount) {
                    await updateCurrentAccount(axios, finalData);
                    window.location.reload();
                } else {
                    const updatedAccount = await managementUpdateAccountByUniqueIdentifier(axios, account.unique_identifier, finalData);
                    if (!setAccount) {
                        throw new Error('setAccount function is required to update account');
                    }
                    setAccount(updatedAccount);
                    setSaveSuccess(copy.Account.details.updated);
                }
            } catch (exception) {
                console.error(exception);
                const errorMessage = exception instanceof APIError ? exception.message : 'An unknown error has occurred. Please try again.';
                setErrorSaving(errorMessage);
            } finally {
                setSaving(false);
            }
        }
    }, [saving, account, axios, navigate, setAccount, isForCurrentAccount]);

    return (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 lg:gap-7.5">
            <div className="col-span-2">
                <div className="flex flex-col gap-5 lg:gap-7.5">
                    <form className="card" onSubmit={onManageAccountFormSubmitted}>
                        <div className="card-header">
                            <h3 className="card-title">
                                {copy.Account.details.accountInfo}
                            </h3>
                        </div>
                        <div className="card-body grid gap-7.5 py-5 lg:py-7.5">
                            <div className="flex flex-col gap-4">
                                {/* Always show name field */}
                                <div>
                                    <label
                                        htmlFor="account_name"
                                        className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                                        {`${copy.Account.details.businessName} (${copy.Common.labels.required})`}
                                    </label>
                                    <input
                                        id="account_name"
                                        name="account_name"
                                        type="text"
                                        className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                        placeholder={copy.Account.details.businessName}
                                        required
                                        autoComplete='off'
                                        autoFocus={!account && canEdit}
                                        defaultValue={account?.name || ''}
                                        disabled={!canEdit}
                                    />
                                </div>
                                {(currentUser.is_agency_admin || currentUser.is_super_super_user) && (<>
                                    <div>
                                        <input
                                            id="manually_disabled_free_trial"
                                            name="manually_disabled_free_trial"
                                            type="checkbox"
                                            className="mr-2"
                                            defaultChecked={!!account?.public_data?.manually_disabled_free_trial}
                                        />
                                        <label htmlFor="manually_disabled_free_trial" className="text-sm font-medium text-gray-900 dark:text-white">
                                            {copy.Common.labels.disabled} free trial for this {copy.Common.navigation.account}
                                        </label>
                                    </div>
                                    <div>
                                        <input
                                            id="enable_free_access"
                                            name="enable_free_access"
                                            type="checkbox"
                                            className="mr-2"
                                            defaultChecked={!!account?.public_data?.enable_free_access}
                                        />
                                        <label htmlFor="enable_free_access" className="text-sm font-medium text-gray-900 dark:text-white">
                                            Enable free access for this {copy.Common.navigation.account}
                                        </label>
                                    </div>
                                </>
                                )}
                                {/* Advanced options toggle */}
                                {canEdit && (
                                    <div className="flex items-center mt-2">
                                        <input
                                            id="view_advanced_options"
                                            type="checkbox"
                                            className="mr-2"
                                            checked={viewAdvancedOptions}
                                            onChange={() => setViewAdvancedOptions(!viewAdvancedOptions)}
                                        />
                                        <label htmlFor="view_advanced_options" className="text-sm font-medium text-gray-900 dark:text-white">
                                            {copy.CreateService.form.viewAdvancedOptionsLabel}
                                        </label>
                                    </div>
                                )}
                                {/* Advanced fields */}
                                <div style={{ display: viewAdvancedOptions ? 'block' : 'none' }}>
                                    <div>
                                        <label
                                            htmlFor="public_data_link_to_obituary_listing"
                                            className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                                            {`Link to obituary listing page (optional)`}
                                        </label>
                                        <input
                                            id="public_data_link_to_obituary_listing"
                                            name="public_data_link_to_obituary_listing"
                                            type="text"
                                            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                            placeholder="https://www.solemn-oaks.com/obituaries"
                                            required={false}
                                            autoComplete='off'
                                            defaultValue={account?.public_data?.link_to_obituary_listing || ''}
                                            pattern="https?://.*"
                                        />
                                        <div className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                                            A full URL is required (e.g. https://www.solemn-oaks.com/obituaries)
                                        </div>
                                    </div>
                                    <div>
                                        <label
                                            htmlFor="public_data_obituary_detail_avatar_url_xpath_selector"
                                            className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                                            {`Obituary detail avatar URL XPath selector (optional)`}
                                        </label>
                                        <input
                                            id="public_data_obituary_detail_avatar_url_xpath_selector"
                                            name="public_data_obituary_detail_avatar_url_xpath_selector"
                                            type="text"
                                            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                            placeholder="//img[@class='avatar']"
                                            required={false}
                                            autoComplete='off'
                                            defaultValue={account?.public_data?.obituary_detail_avatar_url_xpath_selector || ''}
                                        />
                                        <div className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                                            An XPath selector to the avatar image on the obituary detail page
                                        </div>
                                    </div>
                                    <div>
                                        <label
                                            htmlFor="public_data_downloadable_pdf_first_page_quote"
                                            className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                                        >
                                            {`Downloadable PDF First Page Quote (optional)`}
                                        </label>
                                        <input
                                            id="public_data_downloadable_pdf_first_page_quote"
                                            name="public_data_downloadable_pdf_first_page_quote"
                                            type="text"
                                            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm 
               rounded-lg focus:ring-blue-500 focus:border-blue-500 block 
               w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 
               dark:placeholder-gray-400 dark:text-white 
               dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                            placeholder="e.g. I have fought the good fight, I have finished the course, I have kept the faith."
                                            required={false}
                                            autoComplete='off'
                                            defaultValue={account?.public_data?.downloadable_pdf_first_page_quote || ''}
                                        />
                                        <div className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                                            A short quote to appear on the first page of the downloadable PDF.
                                        </div>
                                    </div>
                                    <div className="mt-4">
                                        <label
                                            htmlFor="public_data_downloadable_pdf_final_page_quote"
                                            className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                                        >
                                            {`Downloadable PDF Final Page Quote (optional)`}
                                        </label>
                                        <input
                                            id="public_data_downloadable_pdf_final_page_quote"
                                            name="public_data_downloadable_pdf_final_page_quote"
                                            type="text"
                                            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm 
               rounded-lg focus:ring-blue-500 focus:border-blue-500 block 
               w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 
               dark:placeholder-gray-400 dark:text-white 
               dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                            placeholder="e.g. Life is not measured by the breaths we take
but by the moments that take our breath away"
                                            required={false}
                                            autoComplete='off'
                                            defaultValue={account?.public_data?.downloadable_pdf_final_page_quote || ''}
                                        />
                                        <div className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                                            A short quote to appear on the final page of the downloadable PDF.
                                        </div>
                                    </div>
                                    <div className="mt-4">
                                        <label
                                            htmlFor="public_data_downloadable_pdf_background_color_hex"
                                            className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                                        >
                                            {`Downloadable PDF Background Color Hex (optional)`}
                                        </label>
                                        <input
                                            id="public_data_downloadable_pdf_background_color_hex"
                                            name="public_data_downloadable_pdf_background_color_hex"
                                            type="text"
                                            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm 
               rounded-lg focus:ring-blue-500 focus:border-blue-500 block 
               w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 
               dark:placeholder-gray-400 dark:text-white 
               dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                            placeholder="#1E3A8A"
                                            required={false}
                                            autoComplete="off"
                                            defaultValue={account?.public_data?.downloadable_pdf_background_color_hex || ''}
                                        />
                                        <div className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                                            Hex color code for the background of the downloadable PDF (e.g., #1E3A8A).
                                        </div>
                                    </div>
                                    <div className="mt-4">
                                        <label
                                            htmlFor="public_data_downloadable_pdf_text_color_hex"
                                            className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                                        >
                                            {`Downloadable PDF Text Color Hex (optional)`}
                                        </label>
                                        <input
                                            id="public_data_downloadable_pdf_text_color_hex"
                                            name="public_data_downloadable_pdf_text_color_hex"
                                            type="text"
                                            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm 
               rounded-lg focus:ring-blue-500 focus:border-blue-500 block 
               w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 
               dark:placeholder-gray-400 dark:text-white 
               dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                            placeholder="#FFFFFF"
                                            required={false}
                                            autoComplete="off"
                                            defaultValue={account?.public_data?.downloadable_pdf_text_color_hex || ''}
                                        />
                                        <div className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                                            Hex color code for the text in the downloadable PDF (e.g., #FFFFFF).
                                        </div>
                                    </div>
                                    <div className="mt-4">
                                        <label
                                            htmlFor="public_data_downloadable_pdf_last_page_logo_url"
                                            className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                                        >
                                            {`Downloadable PDF Last Page Logo URL (optional)`}
                                        </label>
                                        <input
                                            id="public_data_downloadable_pdf_last_page_logo_url"
                                            name="public_data_downloadable_pdf_last_page_logo_url"
                                            type="url"
                                            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm 
               rounded-lg focus:ring-blue-500 focus:border-blue-500 block 
               w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 
               dark:placeholder-gray-400 dark:text-white 
               dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                            placeholder="https://www.example.com/logo.png"
                                            required={false}
                                            autoComplete="off"
                                            defaultValue={account?.public_data?.downloadable_pdf_last_page_logo_url || ''}
                                        />
                                        <div className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                                            URL to a logo or image to display on the last page of the downloadable PDF.
                                        </div>
                                    </div>
                                    <div className="mt-4">
                                        <label
                                            htmlFor="public_data_friends_and_relatives_pdf_footer_logo_url"
                                            className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                                        >
                                            {`Friends and Relatives PDF Footer Logo URL (optional)`}
                                        </label>
                                        <input
                                            id="public_data_friends_and_relatives_pdf_footer_logo_url"
                                            name="public_data_friends_and_relatives_pdf_footer_logo_url"
                                            type="url"
                                            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm 
                                               rounded-lg focus:ring-blue-500 focus:border-blue-500 block 
                                               w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 
                                               dark:placeholder-gray-400 dark:text-white 
                                               dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                            placeholder="https://www.example.com/footer-logo.png"
                                            required={false}
                                            autoComplete="off"
                                            defaultValue={account?.public_data?.friends_and_relatives_pdf_footer_logo_url || ''}
                                        />
                                        <div className="mt-2">
                                            <button
                                                type="button"
                                                className="text-blue-700 underline hover:text-blue-900"
                                                onClick={() => setShowLogoUploader(true)}
                                            >
                                                {copy.Account.details.uploadLogo}
                                            </button>
                                        </div>
                                        {showLogoUploader && (
                                            <div className="mb-5">
                                                <FuneralHomeLogoUploader
                                                    account={account}
                                                    onClose={() => setShowLogoUploader(false)}
                                                />
                                            </div>
                                        )}
                                        <div className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                                            URL to a logo or image to display in the footer of the Friends and Relatives downloadable PDF.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="card-footer py-3.5 flex flex-col lg:flex-row items-center justify-between">
                            <div>
                                {errorSaving && <div className="text-red-500 text-sm font-medium">{errorSaving}</div>}
                                {saveSuccess && <div className="text-green-500 text-sm font-medium">{saveSuccess}</div>}
                                {!canEdit && <div className="text-gray-500 text-sm">View only - contact an administrator to make changes</div>}
                            </div>
                            <div>
                                {canEdit && (
                                    account ? <button
                                        type="submit"
                                        className="btn btn-sm btn-primary"
                                        disabled={saving}
                                    >
                                        {!saving ? copy.Account.details.update : copy.Account.details.updating}
                                    </button> : <button
                                        type="submit"
                                        className="btn btn-sm btn-primary"
                                        disabled={saving}
                                    >
                                        {!saving ? copy.Management.addAccount : `${copy.Common.labels.saving}...`}
                                    </button>
                                )}
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
